// pages/FormViewerPage/hooks/useFormViewer/actionsCoupon.ts — Pratinjau & terapkan kupon.
// Validasi kode (read-only) via RPC; diskon final dihitung otoritatif lagi di submit_public_order.
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../../../../supabase/client';
import { computeDiscountAmount } from '../../services/formViewerService';
import type { FormViewerState } from './state';
import type { FormViewerComputed } from './computed';

export function useFormViewerCoupon(s: FormViewerState, c: FormViewerComputed) {
    const {
        form, quantity, customerData,
        couponCode, couponApplied, couponError,
        setCouponCode, setCouponApplied, setCouponError, setCouponApplying, couponApplying,
    } = s;

    const subtotalUnit = c.currentCombination?.sellingPrice ?? form?.productPrice ?? 0;
    const subtotalLine = subtotalUnit * (quantity || 1);

    const discountAmount = useMemo(
        () => computeDiscountAmount(couponApplied?.rawDiscount || 0, subtotalLine, c.shippingCost, c.codFee),
        [couponApplied?.rawDiscount, subtotalLine, c.shippingCost, c.codFee],
    );

    // Subtotal hilang (varian/qty jadi 0) → kupon tak relevan lagi, lepas.
    useEffect(() => {
        if (couponApplied && subtotalLine <= 0) {
            setCouponApplied(null);
            setCouponError('');
        }
    }, [couponApplied, subtotalLine, setCouponApplied, setCouponError]);

    // Auto-terap diskon FU4 (dari chat) tanpa customer perlu tahu kodenya: begitu nomor WA
    // diisi, cek kode FU4-10K untuk nomor itu. Validasi otoritatif tetap di submit_public_order.
    const fu4TriedPhone = useRef('');
    useEffect(() => {
        const phone = String(customerData?.whatsapp || '').replace(/\D/g, '');
        if (phone.length < 10 || subtotalLine <= 0) return;
        if (fu4TriedPhone.current === phone) return;
        const typed = (couponCode || '').trim().toUpperCase();
        if (typed && typed !== 'FU4-10K') return;
        fu4TriedPhone.current = phone;
        let cancelled = false;
        (async () => {
            try {
                const { data, error } = await supabase.rpc('validate_form_voucher_code', {
                    p_code: 'FU4-10K', p_customer_phone: phone, p_base_price: subtotalLine,
                });
                const res: any = Array.isArray(data) ? data[0] : data;
                if (!cancelled && !error && res?.success) {
                    setCouponCode('FU4-10K');
                    setCouponApplied({ code: 'FU4-10K', rawDiscount: Number(res.discountAmount) || 0 });
                    setCouponError('');
                }
            } catch { /* bukan error user - diamkan */ }
        })();
        return () => { cancelled = true; };
    }, [customerData?.whatsapp, subtotalLine, couponCode, setCouponCode, setCouponApplied, setCouponError]);

    const applyCoupon = useCallback(async () => {
        const code = (couponCode || '').trim().toUpperCase();
        if (!code) return;
        setCouponApplying(true);
        setCouponError('');
        try {
            const { data, error } = await supabase.rpc('validate_form_voucher_code', {
                p_code: code,
                p_customer_phone: customerData?.whatsapp || '',
                p_base_price: subtotalLine,
            });
            if (error) {
                setCouponApplied(null);
                setCouponError('Gagal memeriksa kode, coba lagi.');
                return;
            }
            const res: any = Array.isArray(data) ? data[0] : data;
            if (!res?.success) {
                setCouponApplied(null);
                setCouponError(res?.error || 'Kode kupon tidak valid');
                return;
            }
            setCouponApplied({ code: String(res.voucherCode || code), rawDiscount: Number(res.discountAmount) || 0 });
        } catch {
            setCouponApplied(null);
            setCouponError('Gagal memeriksa kode, coba lagi.');
        } finally {
            setCouponApplying(false);
        }
    }, [couponCode, subtotalLine, customerData?.whatsapp, setCouponApplied, setCouponError, setCouponApplying]);

    const clearCoupon = useCallback(() => {
        setCouponApplied(null);
        setCouponError('');
        setCouponCode('');
    }, [setCouponApplied, setCouponError, setCouponCode]);

    return { discountAmount, applyCoupon, clearCoupon, couponApplying, couponApplied, couponError, couponCode, setCouponCode };
}
